import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Grid, Paper, Button
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';

const HostDashboard = () => {

  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();

  const [pendingVisits, setPendingVisits] = useState(0);
  const [totalVisits, setTotalVisits] = useState(0);
  const [tenantType, setTenantType] = useState(null);
  const [facilityType, setFacilityType] = useState(null);
  const [roleLabel, setRoleLabel] = useState('User');
  const [dashboardTitle, setDashboardTitle] = useState('Dashboard');

  const facilityFromNavigation = location.state?.facilityId;
  const activeFacilityId = user?.facilityId || facilityFromNavigation;

  // 🔹 Load Tenant & Facility Info
  useEffect(() => {
    const loadTenantAndFacilityInfo = async () => {
      if (!user?.tenantId) return;

      try {
        const tenantSnap = await getDocs(
          query(collection(db, "tenantmaster"), where("tenantId", "==", user.tenantId))
        );

        if (!tenantSnap.empty) {
          setTenantType(tenantSnap.docs[0].data().tenantType);
        }

        if (user.facilityId) {
          const facilityRef = doc(
            db,
            "tenantmaster",
            user.tenantId,
            "facilities",
            user.facilityId
          );

          const facilitySnap = await getDoc(facilityRef);

          if (facilitySnap.exists()) {
            setFacilityType(facilitySnap.data().facilityType);
          }
        }
      } catch (err) {
        console.error("Failed to load tenant/facility info", err);
      }
    };

    loadTenantAndFacilityInfo();
  }, [user]);

  // 🔹 Determine Role Label
  useEffect(() => {
    if (!tenantType && !facilityType) return;

    let finalRole = 'User';
    let finalTitle = 'Dashboard';

    if (tenantType === 200) {
      finalRole = 'Host';
      finalTitle = 'Host Dashboard';
    } else if (tenantType === 100) {
      finalRole = 'Member';
      finalTitle = 'Member Dashboard';
    } else if (tenantType === 300) {
      if (facilityType === 200) {
        finalRole = 'Host';
        finalTitle = 'Host Dashboard';
      } else if (facilityType === 100) {
        finalRole = 'Member';
        finalTitle = 'Member Dashboard';
      }
    }

    setRoleLabel(finalRole);
    setDashboardTitle(finalTitle);
  }, [tenantType, facilityType]);

  // 🔹 Fetch Visits
  useEffect(() => {
    if (!user || (user.role !== 'host' && user.role !== 'tenant')) return;

    const fetchCounts = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'visit_approvals'),
          where('hostUserID', '==', user.uid)
        ));

        setPendingVisits(snap.size);
        setTotalVisits(snap.size);
      } catch (error) {
        console.error(error);
      }
    };

    fetchCounts();
  }, [user]);

  if (!user || (user.role !== 'host' && user.role !== 'tenant')) return null;

  const actions = [
    {
      title: "Pending Approvals",
      description: `${roleLabel === 'Host' ? 'Review and approve' : 'Review and manage'} visitor requests`,
      path: "/visit-approvals"
    },
    {
      title: "Visit History",
      description: "View past visitor records",
      path: "/host/visits"
    }
  ];

  return (
    <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>

      <Container maxWidth="lg">

        {/* HEADER */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{
            fontSize: "2rem",
            fontWeight: 900,
            fontFamily: "'Unbounded', sans-serif",
            color: "#1c1400"
          }}>
            {dashboardTitle}
          </Typography>

          <Typography sx={{ color: "#8a7a40", mt: 0.5 }}>
            Welcome, {user?.userName || user?.email}
          </Typography>
        </Box>

        {/* ACTION CARDS */}
        <Grid container spacing={3}>
          {actions.map((action, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Paper sx={{
                p: 3,
                borderRadius: "14px",
                border: "1px solid #f0d060",
                background: "#ffffff",
                transition: "0.3s",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.08)"
                }
              }}>

                <Box>
                  <Typography sx={{
                    fontWeight: 700,
                    color: "#1c1400"
                  }}>
                    {action.title}
                  </Typography>

                  <Typography sx={{
                    color: "#6b7280",
                    fontSize: "0.9rem",
                    mt: 1
                  }}>
                    {action.description}
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  sx={{
                    mt: 2,
                    background: "#1c1400",
                    color: "#fde047",
                    borderRadius: "8px",
                    fontWeight: 600,
                    "&:hover": {
                      background: "#f59e0b",
                      color: "#1c1400"
                    }
                  }}
                  onClick={() =>
                    navigate(action.path, {
                      state: { facilityId: activeFacilityId }
                    })
                  }
                >
                  Access
                </Button>

              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* INFO BOX */}
        <Box sx={{ mt: 4 }}>
          <Paper sx={{
            p: 3,
            borderRadius: "12px",
            background: "#fffbea",
            border: "1px solid #f0d060"
          }}>
            <Typography sx={{ color: "#6b7280" }}>
              As a {roleLabel.toLowerCase()}, you can manage visitor requests and view your visit history.
            </Typography>
          </Paper>
        </Box>

      </Container>
    </Box>
  );
};

export default HostDashboard;